import type { NoticeVO } from '@/api/system/notice/types';
import i18n from '@/lang/index';

export const EVENT_GLOBAL_NOTICE_RECEIVED = 'globalNoticeReceived';

export type NoticeCategory = 'notice' | 'announcement' | 'update';

export interface NotifyMessage {
  id: string;
  type: 'info' | 'warn' | 'success';
  icon: string;
  iconColor: 'blue' | 'orange' | 'green';
  title: string;
  tag: string;
  tagClass: string;
  desc: string;
  time: string;
  category: NoticeCategory;
  unread: boolean;
  isNew: boolean;
  fingerprint: string;
  contentHtml: string;
  notice: NoticeVO;
}

export interface NoticeNewRecord {
  id: string;
  category: NoticeCategory;
  fingerprint: string;
}

/**
 * noticeType 与展示元信息的映射。
 * icon/tag 属于界面文案，这里只存 i18n key；模块常量在应用启动即求值，
 * 直接存译文会固定在初始语言，因此在 toNotifyMessage 渲染前统一翻译。
 */
interface NoticeTypeMeta {
  /** 展示分类，用于本地 NEW 记录与 tab 统计 */
  category: NoticeCategory;
  /** 消息列表角标图标的 i18n key */
  iconKey: string;
  /** 角标图标颜色主题 */
  iconColor: 'blue' | 'orange' | 'green';
  /** 消息分类标签（通知/公告/更新记录）的 i18n key */
  tagKey: string;
  /** 标签样式类名 */
  tagClass: string;
  /** 消息图标类型 */
  type: 'info' | 'warn' | 'success';
}

const NOTICE_TYPE_META: Record<string, NoticeTypeMeta> = {
  '1': {
    category: 'notice',
    iconKey: 'headerWidgets.noticeIconBadge',
    iconColor: 'blue',
    tagKey: 'system.notify.tabNotice',
    tagClass: 'notice',
    type: 'info'
  },
  '2': {
    category: 'announcement',
    iconKey: 'headerWidgets.announcementIconBadge',
    iconColor: 'orange',
    tagKey: 'system.notify.tabAnnouncement',
    tagClass: 'announcement',
    type: 'warn'
  },
  '3': {
    category: 'update',
    iconKey: 'headerWidgets.updateIconBadge',
    iconColor: 'green',
    tagKey: 'system.notify.tabUpdate',
    tagClass: 'update',
    type: 'success'
  }
};

const DEFAULT_NOTICE_META = NOTICE_TYPE_META['1'];

/**
 * 翻译顶部通知的展示文案（非组件模块，走全局 i18n 实例）。
 *
 * @param key i18n 完整键名
 * @returns 当前语言下的文案
 */
function translateNoticeText(key: string): string {
  return String(i18n.global.t(key));
}

/**
 * 判断公告是否处于可提醒状态。
 * 后端约定 status=0 为正常展示，其他状态不应恢复或生成 NEW，避免停用公告继续占用顶部红点。
 *
 * @param notice 后端通知公告对象
 * @returns true 表示可以参与 NEW 提醒
 */
export function isActiveNotice(notice: NoticeVO): boolean {
  return String(notice.status) === '0';
}

/**
 * 将任意通知 ID 统一成字符串。
 * 后端历史数据里 noticeId 同时存在 number 和 string，统一后才能稳定做已读、去重和 socket 合并。
 *
 * @param id 后端返回的通知公告 ID
 * @returns 字符串 ID；空值返回空字符串
 */
export function normalizeNoticeId(id: unknown): string {
  return String(id ?? '');
}

/**
 * 将通知正文 HTML 转成列表摘要文本。
 * 顶部通知列表只需要短摘要，先去掉标签再折叠空白，避免富文本标签直接展示到列表里。
 *
 * @param html 后端富文本 HTML 内容
 * @returns 去标签后的纯文本摘要
 */
export function stripNoticeHtml(html: unknown): string {
  return String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * 生成通知内容指纹。
 * socket 可能因重连补发同一条公告，指纹用关键业务字段组成，既能拦截完全重复推送，也允许同 ID 内容更新重新提醒。
 *
 * @param notice 后端通知公告对象
 * @returns 可持久化比较的内容指纹
 */
export function getNoticeFingerprint(notice: NoticeVO): string {
  return JSON.stringify([
    normalizeNoticeId(notice.noticeId),
    notice.noticeTitle || '',
    notice.noticeType || '',
    notice.noticeContent || '',
    notice.createTime || ''
  ]);
}

/**
 * 将后端通知公告对象适配为顶部 Notify 组件可直接渲染的数据。
 * 这里集中维护 noticeType 与展示分类的映射，避免列表、socket、详情抽屉各处重复写分支。
 *
 * @param notice listNotice 或 /global/notice 返回的单条通知公告数据
 * @param options 消息展示选项；接口初始化可传 unread=false，socket 新推送可传 isNew 标记 NEW 角标
 * @returns 顶部通知消息对象，默认标记为未读
 */
export function toNotifyMessage(notice: NoticeVO, options: { isNew?: boolean; unread?: boolean } = {}): NotifyMessage {
  const meta = NOTICE_TYPE_META[String(notice.noticeType)] || DEFAULT_NOTICE_META;
  // 展示文案在适配时统一翻译，保证语言切换后新适配的消息使用最新语言
  const tag = translateNoticeText(meta.tagKey);
  return {
    id: normalizeNoticeId(notice.noticeId),
    type: meta.type,
    icon: translateNoticeText(meta.iconKey),
    iconColor: meta.iconColor,
    title: notice.noticeTitle || tag,
    tag,
    tagClass: meta.tagClass,
    desc: stripNoticeHtml(notice.noticeContent),
    time: notice.createTime || '',
    category: meta.category,
    unread: options.unread ?? true,
    isNew: Boolean(options.isNew),
    fingerprint: getNoticeFingerprint(notice),
    contentHtml: notice.noticeContent || '',
    notice
  };
}

/**
 * 从通知消息创建可持久化的 NEW 记录。
 * 记录中保留 category 是为了刷新后仍能按 Tab 统计未读角标，保留 fingerprint 是为了识别重复 socket 推送。
 *
 * @param message 已适配的顶部通知消息
 * @returns 可写入 localStorage 的 NEW 记录
 */
export function createNoticeNewRecord(message: NotifyMessage): NoticeNewRecord {
  return {
    id: message.id,
    category: message.category,
    fingerprint: message.fingerprint
  };
}

/**
 * 解析持久化 NEW 记录。
 * 对异常 JSON、旧格式或缺失字段做兜底过滤，避免本地缓存损坏导致通知面板渲染失败。
 *
 * @param raw localStorage 中保存的原始字符串
 * @returns 去重后的 NEW 记录
 */
export function parseNoticeNewRecords(raw: string | null): NoticeNewRecord[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.reduce<NoticeNewRecord[]>((records, item) => {
      if (!item || typeof item !== 'object') return records;
      const id = normalizeNoticeId((item as NoticeNewRecord).id);
      const category = (item as NoticeNewRecord).category;
      const fingerprint = String((item as NoticeNewRecord).fingerprint || '');
      if (!id || !['notice', 'announcement', 'update'].includes(category) || !fingerprint) return records;
      return upsertNoticeNewRecord(records, { id, category, fingerprint });
    }, []);
  } catch {
    return [];
  }
}

/**
 * 序列化 NEW 记录。
 * 写入前按 id 去重，保证同一公告更新时只保留最新 fingerprint。
 *
 * @param records NEW 记录列表
 * @returns 可保存到 localStorage 的 JSON 字符串
 */
export function stringifyNoticeNewRecords(records: NoticeNewRecord[]): string {
  return JSON.stringify(records.reduce<NoticeNewRecord[]>((next, record) => upsertNoticeNewRecord(next, record), []));
}

/**
 * 插入或更新单条 NEW 记录。
 * 同 ID 代表同一公告，新的 fingerprint 覆盖旧记录，用于同一公告内容更新后重新提醒。
 *
 * @param records 当前 NEW 记录
 * @param record 需要写入的 NEW 记录
 * @returns 更新后的新数组
 */
export function upsertNoticeNewRecord(records: NoticeNewRecord[], record: NoticeNewRecord): NoticeNewRecord[] {
  return [record, ...records.filter((item) => item.id !== record.id)];
}

/**
 * 移除单条 NEW 记录。
 * 点击查看详情后调用，确保刷新页面后该公告不再显示 NEW 和未读红点。
 *
 * @param records 当前 NEW 记录
 * @param id 已查看的公告 ID
 * @returns 移除后的新数组
 */
export function removeNoticeNewRecord(records: NoticeNewRecord[], id: unknown): NoticeNewRecord[] {
  const normalizedId = normalizeNoticeId(id);
  return records.filter((record) => record.id !== normalizedId);
}

/**
 * 根据持久化 NEW 记录恢复列表消息状态。
 * 接口初始化默认已读，只有仍存在 NEW 记录的公告才恢复未读和 NEW 角标。
 *
 * @param messages 接口适配后的消息列表
 * @param records 持久化 NEW 记录
 * @returns 应用 NEW 状态后的新列表
 */
export function applyNoticeNewRecords(messages: NotifyMessage[], records: NoticeNewRecord[]): NotifyMessage[] {
  const recordMap = new Map(records.map((record) => [record.id, record]));
  return messages.map((message) => {
    const record = recordMap.get(message.id);
    if (!record || !isActiveNotice(message.notice)) return { ...message, unread: false, isNew: false };
    return { ...message, unread: true, isNew: true };
  });
}

/**
 * 判断 socket 推送是否为重复内容。
 * 同 noticeId 且 fingerprint 完全一致时视为重复推送，不再重新置顶或标记 NEW。
 *
 * @param records 已处理过的推送记录
 * @param notice socket 推送的通知公告
 * @returns true 表示应忽略本次推送
 */
export function isDuplicateNoticePush(records: NoticeNewRecord[], notice: NoticeVO): boolean {
  const id = normalizeNoticeId(notice.noticeId);
  const fingerprint = getNoticeFingerprint(notice);
  return records.some((record) => record.id === id && record.fingerprint === fingerprint);
}

/**
 * 合并一条实时通知到现有通知列表。
 * 同 ID 表示后端同一条公告的更新，必须用最新内容覆盖并置顶；否则插入顶部，让用户优先看到最新推送。
 *
 * @param messages 当前顶部通知列表
 * @param incoming socket 或接口转换后的新消息
 * @returns 合并后的新数组，不直接修改原数组，便于 Vue 追踪变更
 */
export function mergeNoticeMessage(messages: NotifyMessage[], incoming: NotifyMessage): NotifyMessage[] {
  const filtered = messages.filter((message) => message.id !== incoming.id);
  return [incoming, ...filtered];
}

/**
 * 安全解析 websocket 原始载荷。
 * socket 可能传字符串，也可能被上层 useWebSocket 处理成对象；无 noticeId 的数据不参与联动，避免污染通知列表。
 *
 * @param payload socket 回调拿到的原始数据
 * @returns 合法通知公告对象；异常或缺少 noticeId 时返回 null
 */
export function normalizeNoticePayload(payload: unknown): NoticeVO | null {
  let parsed = payload;
  if (typeof payload === 'string') {
    try {
      parsed = JSON.parse(payload);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object') return null;
  const notice = parsed as NoticeVO;
  return normalizeNoticeId(notice.noticeId) ? notice : null;
}
